// api/common/signalRServiceBase.ts
import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { API_CONFIG } from '../config';
import { convertToAbsoluteUrl } from '@/utils/urlUtils';

export interface SignalRConnectionOptions {
  hubName: string;
  transportStrategies?: TransportStrategy[];
  reconnectAttempts?: number;
  reconnectInterval?: number;
  loggingLevel?: LogLevel;
  onAuthTokenRequired?: () => Promise<string | null>;
  onAuthTokenMissing?: () => void;
}

export interface TransportStrategy {
  name: string;
  transport: HttpTransportType;
  skipNegotiation: boolean;
}

export class SignalRServiceBase {
  protected connection: HubConnection | null = null;
  protected isConnecting: boolean = false;
  protected isManuallyDisconnected: boolean = false;
  protected reconnectAttempts: number = 0;
  protected maxReconnectAttempts: number;
  protected reconnectInterval: number;
  protected hubName: string;
  protected fullHubUrl: string; // Added to store full URL
  protected transportStrategies: TransportStrategy[];
  protected loggingLevel: LogLevel;
  protected onAuthTokenRequired: () => Promise<string | null>;
  protected onAuthTokenMissing: () => void;
  
  protected eventCallbacks: Map<string, Function[]> = new Map();
  protected subscriptions: Set<string> = new Set();
  protected connectionState: string = 'Disconnected';
  protected connectionRetryTimeout: number | null = null;

  constructor(options: SignalRConnectionOptions) {
    // FIXED: Properly construct the full URL
    const baseUrl = convertToAbsoluteUrl(API_CONFIG.baseURL);
    this.fullHubUrl = `${baseUrl}${options.hubName}`;
    this.hubName = options.hubName;
    
    this.maxReconnectAttempts = options.reconnectAttempts || 3;
    this.reconnectInterval = options.reconnectInterval || 3000;
    this.loggingLevel = options.loggingLevel || LogLevel.Information;
    
    // Default auth handlers
    this.onAuthTokenRequired = options.onAuthTokenRequired || (async () => null);
    this.onAuthTokenMissing = options.onAuthTokenMissing || (() => {
      console.warn('No auth token handler provided');
    });
    
    // WebSockets should be first, with skipNegotiation only for WebSockets
    this.transportStrategies = options.transportStrategies || [
      { 
        name: 'WebSockets', 
        transport: HttpTransportType.WebSockets,
        skipNegotiation: true // WebSockets can skip negotiation
      },
      { 
        name: 'LongPolling', 
        transport: HttpTransportType.LongPolling,
        skipNegotiation: false // Don't skip for LongPolling
      },
      { 
        name: 'ServerSentEvents', 
        transport: HttpTransportType.ServerSentEvents,
        skipNegotiation: false // Don't skip for SSE
      },
    ];
  }

  async connect(): Promise<boolean> {
    // Prevent multiple connection attempts
    if (this.connection?.state === 'Connected') {
      console.log(`🔄 ${this.hubName}: Already connected`);
      return true;
    }
    
    if (this.isConnecting) {
      console.log(`🔄 ${this.hubName}: Already connecting`);
      return false;
    }

    // Check if manually disconnected
    if (this.isManuallyDisconnected) {
      console.log(`🔌 ${this.hubName}: Connection was manually disconnected, not reconnecting`);
      return false;
    }

    try {
      this.isConnecting = true;
      
      // Get authentication token
      const token = await this.getAuthToken();
      
      if (!token) {
        console.warn(`⚠️ ${this.hubName}: No authentication token available, will not connect`);
        this.isConnecting = false;
        this.onAuthTokenMissing();
        return false;
      }

      console.log(`🔗 ${this.hubName}: Connecting to:`, this.fullHubUrl);
      console.log(`🔑 Token available:`, token ? 'Yes (length: ' + token.length + ')' : 'No');

      // Try to connect with available transports
      const connection = await this.tryConnectWithFallback(this.fullHubUrl, token);
      
      if (!connection) {
        throw new Error('Failed to establish connection with any transport');
      }

      this.connection = connection;
      this.connectionState = 'Connected';
      
      console.log(`✅ ${this.hubName}: Connected successfully`);
      console.log(`📡 Connection ID:`, this.connection.connectionId);
      console.log(`🚀 Transport:`, this.getTransportType());

      this.setupCommonEventHandlers(connection);
      await this.setupCustomEventHandlers(connection);
      
      this.reconnectAttempts = 0;
      this.isConnecting = false;

      this.triggerEvent('Connected', {
        connectionId: this.connection.connectionId,
        hubUrl: this.fullHubUrl,
        timestamp: new Date(),
        transport: this.getTransportType()
      });

      return true;

    } catch (error: any) {
      console.error(`❌ ${this.hubName}: Connection failed`);
      console.error('Error:', error.message);
      
      this.isConnecting = false;
      this.connectionState = 'Disconnected';
      
      this.triggerEvent('ConnectionError', {
        error: error.message,
        hubName: this.hubName,
        hubUrl: this.fullHubUrl,
        timestamp: new Date(),
        reconnectAttempts: this.reconnectAttempts
      });

      // Try reconnection if not manually disconnected
      if (!this.isManuallyDisconnected && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        console.log(`🔄 Will retry connection in ${this.reconnectInterval}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        
        // Clear any existing timeout
        if (this.connectionRetryTimeout !== null) {
          clearTimeout(this.connectionRetryTimeout);
          this.connectionRetryTimeout = null;
        }
        
        this.connectionRetryTimeout = setTimeout(() => {
          console.log(`🔗 ${this.hubName}: Attempting reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
          this.handleReconnection();
        }, this.reconnectInterval) as unknown as number;
      } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.warn(`⚠️ ${this.hubName}: Max reconnection attempts reached (${this.maxReconnectAttempts})`);
        this.triggerEvent('MaxReconnectAttemptsReached', {
          hubName: this.hubName,
          attempts: this.reconnectAttempts,
          timestamp: new Date()
        });
      }
      
      return false;
    }
  }

  protected async tryConnectWithFallback(hubUrl: string, token: string): Promise<HubConnection | null> {
    let lastError: any = null;

    for (const strategy of this.transportStrategies) {
      try {
        console.log(`🔄 ${this.hubName}: Trying ${strategy.name} transport...`);
        
        const connection = new HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token,
            transport: strategy.transport,
            skipNegotiation: strategy.skipNegotiation,
            withCredentials: true,
            headers: {
              'X-SignalR-User-Agent': 'AgroRange-ReactNative/1.0'
            }
          })
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
              if (retryContext.previousRetryCount === 0) return 0;
              if (retryContext.previousRetryCount === 1) return 2000;
              if (retryContext.previousRetryCount === 2) return 5000;
              return 10000;
            }
          })
          .configureLogging(this.loggingLevel)
          .build();

        console.log(`🚀 ${this.hubName}: Starting connection with ${strategy.name}...`);
        
        // Add timeout for connection attempt
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`Connection timeout for ${strategy.name} (10s)`)), 10000);
        });
        
        const connectionPromise = connection.start();
        
        await Promise.race([connectionPromise, timeoutPromise]);
        
        console.log(`✅ ${this.hubName}: Successfully connected using ${strategy.name}`);
        return connection;

      } catch (error: any) {
        console.error(`❌ ${this.hubName}: ${strategy.name} connection failed:`, error.message);
        
        // Log detailed error for negotiation failures
        if (error.message.includes('negotiation') || error.message.includes('404')) {
          console.warn(`⚠️ Negotiation failed for ${strategy.name}. Check if endpoint exists: ${hubUrl}`);
          console.warn(`   Ensure the server has CORS enabled and SignalR endpoints are mapped`);
        }
        
        lastError = error;
        
        // Wait before trying next strategy
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    throw lastError || new Error('All transport strategies failed');
  }

  protected getTransportType(): string {
    if (!this.connection) return 'Unknown';
    
    try {
      // @ts-ignore - Access internal transport
      const transport = this.connection._connection?.transport;
      return transport ? transport.name : 'Unknown';
    } catch {
      return 'Unknown';
    }
  }

  protected setupCommonEventHandlers(connection: HubConnection): void {
    connection.onclose((error) => {
      console.log(`🔌 ${this.hubName}: Connection closed`, error?.message || '');
      
      this.connection = null;
      this.connectionState = 'Disconnected';
      
      // Only trigger reconnection if not manually disconnected
      if (!this.isManuallyDisconnected) {
        this.triggerEvent('ConnectionClosed', {
          error: error?.message,
          wasClean: error === undefined || error === null,
          timestamp: new Date()
        });
      }
    });

    connection.onreconnecting((error) => {
      console.log(`🔄 ${this.hubName}: Reconnecting...`, error?.message || '');
      this.connectionState = 'Reconnecting';
      this.triggerEvent('Reconnecting', {
        error: error?.message,
        timestamp: new Date()
      });
    });

    connection.onreconnected((connectionId) => {
      console.log(`✅ ${this.hubName}: Reconnected successfully`);
      this.connectionState = 'Connected';
      this.reconnectAttempts = 0;
      this.triggerEvent('Reconnected', {
        connectionId,
        timestamp: new Date()
      });
    });
  }

  protected async setupCustomEventHandlers(connection: HubConnection): Promise<void> {
    // To be overridden by child classes
  }

  protected async handleReconnection(): Promise<void> {
    try {
      console.log(`🔄 ${this.hubName}: Attempting reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      await this.connect();
    } catch (error) {
      console.error(`❌ ${this.hubName}: Reconnection attempt ${this.reconnectAttempts} failed:`, error);
    }
  }

  protected async getAuthToken(): Promise<string | null> {
    try {
      return await this.onAuthTokenRequired();
    } catch (error) {
      console.error(`${this.hubName}: Error getting auth token:`, error);
      return null;
    }
  }

  // Event management
  protected triggerEvent(eventName: string, data: any): void {
    const callbacks = this.eventCallbacks.get(eventName) || [];
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`${this.hubName}: Error in ${eventName} callback:`, error);
      }
    });
  }

  on(eventName: string, callback: Function): void {
    if (!this.eventCallbacks.has(eventName)) {
      this.eventCallbacks.set(eventName, []);
    }
    this.eventCallbacks.get(eventName)!.push(callback);
  }

  off(eventName: string, callback?: Function): void {
    if (!callback) {
      this.eventCallbacks.delete(eventName);
      return;
    }
    
    const callbacks = this.eventCallbacks.get(eventName) || [];
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  // Connection management
  async disconnect(): Promise<void> {
    console.log(`🔌 ${this.hubName}: Disconnecting...`);
    
    // Mark as manually disconnected to prevent reconnection
    this.isManuallyDisconnected = true;
    
    // Clear reconnection timeout
    if (this.connectionRetryTimeout !== null) {
      clearTimeout(this.connectionRetryTimeout);
      this.connectionRetryTimeout = null;
    }
    
    // Clear event callbacks
    this.eventCallbacks.clear();
    this.subscriptions.clear();
    
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log(`✅ ${this.hubName}: Disconnected successfully`);
      } catch (error) {
        console.error(`❌ ${this.hubName}: Error during disconnect:`, error);
      } finally {
        this.connection = null;
        this.connectionState = 'Disconnected';
      }
    }
    
    this.reconnectAttempts = 0;
  }

  // Reset for reconnection (call this after login)
  resetConnection(): void {
    this.isManuallyDisconnected = false;
    this.reconnectAttempts = 0;
    console.log(`🔄 ${this.hubName}: Connection reset, ready for reconnection`);
  }

  async invoke(methodName: string, ...args: any[]): Promise<any> {
    if (this.connection?.state === 'Connected') {
      try {
        console.log(`📤 ${this.hubName}: Invoking ${methodName}`);
        return await this.connection.invoke(methodName, ...args);
      } catch (error) {
        console.error(`❌ ${this.hubName}: Failed to invoke ${methodName}:`, error);
        throw error;
      }
    } else {
      throw new Error(`Cannot invoke ${methodName} - connection not established`);
    }
  }

  send(methodName: string, ...args: any[]): Promise<void> {
    return this.invoke(methodName, ...args);
  }

  // Status getters
  getConnectionState(): string {
    return this.connectionState;
  }

  isConnected(): boolean {
    return this.connectionState === 'Connected';
  }

  getConnectionInfo() {
    return {
      hubName: this.hubName,
      fullHubUrl: this.fullHubUrl,
      state: this.connectionState,
      isConnecting: this.isConnecting,
      isManuallyDisconnected: this.isManuallyDisconnected,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      connectionId: this.connection?.connectionId,
      transport: this.getTransportType(),
      subscriptions: this.subscriptions.size
    };
  }

  // Subscription management
  protected addSubscription(key: string): void {
    this.subscriptions.add(key);
  }

  protected removeSubscription(key: string): void {
    this.subscriptions.delete(key);
  }

  protected clearSubscriptions(): void {
    this.subscriptions.clear();
  }

  getSubscriptionStatus(): { isSubscribed: boolean; subscriptions: string[] } {
    return {
      isSubscribed: this.subscriptions.size > 0,
      subscriptions: Array.from(this.subscriptions)
    };
  }

 async heartbeat(): Promise<void> {
  if (this.isConnected()) {
    try {
      // Use a longer timeout for heartbeat to avoid server timeout
      const heartbeatPromise = this.invoke('Heartbeat');
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Heartbeat timeout')), 30000); // 30 second timeout
      });
      
      // Race between heartbeat and timeout
      await Promise.race([heartbeatPromise, timeoutPromise]);
      
      console.log(`💓 ${this.hubName}: Heartbeat sent and acknowledged`);
    } catch (error:any) {
      console.error(`❌ ${this.hubName}: Heartbeat failed:`, error);
      
      // If heartbeat fails, the connection might be dead
      if (error.message.includes('timeout') || error.message.includes('Server timeout')) {
        console.warn(`⚠️ ${this.hubName}: Heartbeat timeout, connection might be unstable`);
      }
    }
  }
}
}